/* eslint-disable max-len */
import { useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import SyntaxHighlighter from "react-syntax-highlighter";
import { docco } from "react-syntax-highlighter/dist/esm/styles/hljs";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  ExternalLink,
  Loader2,
  RefreshCw,
  Search,
} from "lucide-react";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { cn } from "../../../../lib/utils";
import { getIntegrationEvents, updateIntegration } from "../../../../services/integrationEndpoint";
import { updateWorkspace } from "../../../../services/workspace/workspaceEndpoints";
import { queryClient } from "../../../../hooks/QueryHooks";

type Props = {
  data: {
    apiKey: any,
    workspace: any,
  },
  integration: any,
};

type DataSource = {
  id: string;
  name: string;
  icon: string;
  connectionType: string;
  category: string;
  destinations: number;
};

const DATA_SOURCES: DataSource[] = [
  { id: "1", name: "HTTP API", icon: "api", connectionType: "Event streams", category: "Server", destinations: 2 },
  { id: "2", name: "Hyperengage", icon: "he", connectionType: "Event streams", category: "Website", destinations: 3 },
];

const formatEventPreview = (event: Record<string, unknown>) => Object.entries(event || {})
  .map(([key, value]) => `${key}: "${value}"`)
  .join("\n");

const CopyValueButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);

  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard.writeText(text || "");
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
      className="flex h-7 w-7 items-center justify-center rounded-[4px] text-muted-foreground transition-colors hover:bg-muted"
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
};

const Step = ({
  title,
  required,
  description,
  isExpanded,
  isCompleted,
  isActive,
  isLast,
  onToggle,
  children,
}: {
  title: string;
  required?: boolean;
  description: string;
  isExpanded: boolean;
  isCompleted: boolean;
  isActive: boolean;
  isLast: boolean;
  onToggle: () => void;
  children: ReactNode;
}) => (
  <div className="relative flex gap-4">
    <div className="flex flex-shrink-0 flex-col items-center">
      <div
        className={cn(
          "flex h-6 w-6 items-center justify-center rounded-full border-2 transition-colors",
          isCompleted
            ? "border-primary bg-primary"
            : isActive
              ? "border-primary bg-card"
              : "border-border bg-card",
        )}
      >
        {isCompleted ? <Check className="h-3 w-3 text-primary-foreground" /> : null}
        {isActive && !isCompleted ? <div className="h-2 w-2 rounded-full bg-primary" /> : null}
      </div>
      {!isLast ? (
        <div className={cn("min-h-[24px] w-px flex-1", isCompleted ? "bg-primary" : "bg-border")} />
      ) : null}
    </div>

    <div className={cn("min-w-0 flex-1", !isLast ? "pb-6" : "")}> 
      <button
        type="button"
        onClick={onToggle}
        className="group flex w-full items-start justify-between text-left"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className={cn("text-sm font-medium", isActive ? "text-primary" : "text-foreground")}>{title}</h3>
            {required ? <span className="text-[11px] font-medium text-status-cancelled">*</span> : null}
          </div>
          <p className="mt-0.5 pr-4 text-[13px] leading-relaxed text-muted-foreground">{description}</p>
        </div>
        <div className="mt-1 flex-shrink-0 text-muted-foreground">
          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </button>

      {isExpanded ? <div className="mt-4">{children}</div> : null}
    </div>
  </div>
);

const SegmentSteps = ({ data, integration }: Props) => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set([0]));
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [sourceSearch, setSourceSearch] = useState("");
  const [segmentData, setSegmentData] = useState<any>({});

  const getDataMutation = useMutation({
    mutationFn: getIntegrationEvents,
    onSuccess: (response: any) => {
      setSegmentData(response?.data || {});
    },
  });

  const filteredSources = DATA_SOURCES.filter((source) => (
    source.name.toLowerCase().includes(sourceSearch.toLowerCase())
  ));

  const toggleStep = (index: number) => {
    const next = new Set(expandedSteps);
    if (next.has(index)) next.delete(index);
    else next.add(index);
    setExpandedSteps(next);
    setActiveStep(index);
  };

  const handleNext = () => {
    if (activeStep >= 2) return;
    const nextStep = activeStep + 1;
    setActiveStep(nextStep);
    setExpandedSteps((prev) => new Set(prev).add(nextStep));
  };

  const checkEvents = () => {
    if (!data?.workspace?.id || !data?.apiKey?.id) return;

    getDataMutation.mutate({
      workspace_id: data.workspace.id,
      integration_name: "segment_api",
      token: data.apiKey.id,
    });
  };

  const canContinue = Boolean(
    segmentData?.group?.length && segmentData?.identify?.length && segmentData?.track?.length,
  );

  const handleContinue = async () => {
    if (!data?.workspace?.id || !integration?.id) return;

    await updateIntegration({
      created_by_user_id: integration?.created_by_user_id,
      source_name: "segment_api",
      id: integration.id,
      workspace_id: data.workspace?.id,
      setup_complete: true,
      mapping_complete: true,
      events_received: true,
      is_active: true,
    });

    await updateWorkspace({
      workspace_id: data.workspace?.id,
      setup_complete: true,
    });
    // RootPage decides redirects off the DB-backed workspace query; refresh it
    // so a stale "incomplete" read can't bounce the user back to onboarding.
    queryClient.invalidateQueries(["workspace"]);

    navigate(`/home/${data.workspace?.unique_identifier}/headquarters`, { replace: true, state: { setupComplete: true } });
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-[15px] font-semibold text-foreground">Setup checklist</h2>
        <p className="mt-1 text-[13px] text-muted-foreground">
          Configure the Segment destination and verify account, user, and track events before completing setup.
        </p>
        <a
          href="https://app.segment.com/goto-my-workspace/destinations/catalog/actions-hyperengage"
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-flex items-center gap-1 text-[12px] text-primary hover:underline"
        >
          Open Segment destination catalog
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      <div className="border-t border-border pt-5">
        <Step
          title="Select Data Source"
          description="Only showing data sources that are supported by Hyperengage (Actions)."
          isExpanded={expandedSteps.has(0)}
          isCompleted={activeStep > 0}
          isActive={activeStep === 0}
          isLast={false}
          onToggle={() => toggleStep(0)}
        >
          <div className="space-y-3">
            <div className="relative w-56">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Filter by Source..."
                value={sourceSearch}
                onChange={(event) => setSourceSearch(event.target.value)}
                className="h-7 pl-8 text-[13px]"
              />
            </div>

            <div className="overflow-hidden rounded-lg border border-border">
              <div className="grid grid-cols-[32px_1fr_1fr_1fr_1fr_80px] gap-3 border-b border-border bg-muted px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                <span />
                <span>Source</span>
                <span>Connection Type</span>
                <span>Category</span>
                <span>Status</span>
                <span>Destinations</span>
              </div>

              {filteredSources.map((source) => (
                <label
                  key={source.id}
                  className={cn(
                    "grid cursor-pointer grid-cols-[32px_1fr_1fr_1fr_1fr_80px] items-center gap-3 border-b border-border px-4 py-3 transition-colors last:border-b-0",
                    selectedSource === source.id ? "bg-accent-tint" : "hover:bg-muted",
                  )}
                >
                  <div className="flex items-center">
                    <input
                      type="radio"
                      name="segment-source"
                      checked={selectedSource === source.id}
                      onChange={() => setSelectedSource(source.id)}
                      className="h-3.5 w-3.5 accent-primary"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex h-6 w-6 items-center justify-center rounded bg-muted text-[10px] font-semibold text-muted-foreground">
                      {source.icon === "api" ? "API" : "HE"}
                    </div>
                    <span className="text-[13px] font-medium text-foreground">{source.name}</span>
                  </div>
                  <span className="text-[13px] text-muted-foreground">{source.connectionType}</span>
                  <span className="text-[13px] text-muted-foreground">{source.category}</span>
                  <div className="flex items-center gap-1.5">
                    <div className="h-1.5 w-1.5 rounded-full bg-status-cancelled" />
                    <span className="text-[13px] text-muted-foreground">No Recent Data</span>
                  </div>
                  <span className="text-[13px] text-muted-foreground">+{source.destinations}</span>
                </label>
              ))}
            </div>

            <div className="flex justify-end pt-1">
              <Button
                type="button"
                size="sm"
                disabled={!selectedSource}
                onClick={(event) => {
                  event.stopPropagation();
                  handleNext();
                }}
              >
                Next
              </Button>
            </div>
          </div>
        </Step>

        <Step
          title="Add API Key and Workspace Identifier"
          description="After that you need to add the API Key and Workspace Identifier. Once done, turn on the Enable Destination switch."
          isExpanded={expandedSteps.has(1)}
          isCompleted={activeStep > 1}
          isActive={activeStep === 1}
          isLast={false}
          onToggle={() => toggleStep(1)}
        >
          <div className="space-y-5">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <span className="min-w-[160px] text-[13px] font-medium text-foreground">API Key:</span>
                <code className="rounded-[5px] border border-border bg-muted px-2.5 py-1 font-mono text-[13px] text-foreground">
                  {data?.apiKey?.server_analytics_key || "-"}
                </code>
                <CopyValueButton text={data?.apiKey?.server_analytics_key || ""} />
              </div>

              <div className="flex items-center gap-3">
                <span className="min-w-[160px] text-[13px] font-medium text-foreground">Workspace Identifier:</span>
                <code className="rounded-[5px] border border-border bg-muted px-2.5 py-1 font-mono text-[13px] text-foreground">
                  {data?.workspace?.unique_identifier || "-"}
                </code>
                <CopyValueButton text={data?.workspace?.unique_identifier || ""} />
              </div>
            </div>

            <div className="space-y-4 rounded-lg border border-border p-5">
              <h4 className="text-sm font-semibold text-foreground">Basic Settings</h4>
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-[13px] font-medium text-foreground">Name *</label>
                  <Input defaultValue="Hyperengage Test" className="h-8 text-[13px]" readOnly />
                </div>

                <div>
                  <label className="mb-1 block text-[13px] font-medium text-foreground">API Key *</label>
                  <Input value={data?.apiKey?.server_analytics_key || ""} className="h-8 text-[13px]" readOnly />
                </div>

                <div>
                  <label className="mb-1 block text-[13px] font-medium text-foreground">Workspace Identifier *</label>
                  <Input value={data?.workspace?.unique_identifier || ""} className="h-8 text-[13px]" readOnly />
                </div>

                <div className="flex items-center gap-3 pt-1">
                  <label className="text-[13px] font-medium text-foreground">
                    Enable Destination *
                  </label>
                  <p className="flex-1 text-[11px] text-muted-foreground">
                    When enabled, Segment will send data to Hyperengage based on configuration in the Mappings tab.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <Button type="button" size="sm" className="text-[12px]">Save Changes</Button>
                <Button type="button" variant="outline" size="sm" className="gap-1.5 text-[12px]">
                  <Copy className="h-3 w-3" />
                  Copy Settings
                </Button>
              </div>
            </div>

            <div className="flex justify-end pt-1">
              <Button
                type="button"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  handleNext();
                }}
              >
                Next
              </Button>
            </div>
          </div>
        </Step>

        <Step
          title="Await Data Receival in Hyperengage"
          required
          description="Click refresh to check whether data has been received successfully in the backend."
          isExpanded={expandedSteps.has(2)}
          isCompleted={false}
          isActive={activeStep === 2}
          isLast
          onToggle={() => toggleStep(2)}
        >
          <div className="space-y-3">
            {[
              { key: "group", label: "Account events" },
              { key: "identify", label: "User events" },
              { key: "track", label: "Track events" },
            ].map((eventStatus) => {
              const events = segmentData?.[eventStatus.key] || [];
              const received = events.length > 0;

              return (
                <div
                  key={eventStatus.key}
                  className={cn(
                    "rounded-lg border p-4",
                    received ? "border-primary/30 bg-primary/[0.04]" : "border-primary/20 bg-primary/[0.03]",
                  )}
                >
                  <div className="mb-2 flex items-center gap-2">
                    <div className={cn("h-2.5 w-2.5 rounded-full", received ? "bg-status-active" : "bg-primary")} />
                    <span className="text-[13px] font-medium text-foreground">
                      {received ? `${eventStatus.label} received` : `Waiting to receive ${eventStatus.label.toLowerCase()}...`}
                    </span>
                    {received ? (
                      <Badge variant="statusPill" status="success" className="text-[11px]">
                        Received
                      </Badge>
                    ) : null}
                  </div>

                  {received ? (
                    <div className="overflow-x-auto rounded-lg border border-border bg-quaternary">
                      <SyntaxHighlighter showLineNumbers language="javascript" customStyle={{ fontSize: "13px", margin: 0 }} style={docco}>
                        {formatEventPreview(events[0])}
                      </SyntaxHighlighter>
                    </div>
                  ) : null}
                </div>
              );
            })}

            <div className="flex justify-end pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5 text-[12px]"
                onClick={checkEvents}
                disabled={getDataMutation.isLoading}
              >
                {getDataMutation.isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                Refresh
              </Button>
            </div>
          </div>
        </Step>
      </div>

      <div className="flex items-center justify-end gap-3 border-t border-border pt-4">
        <Button type="button" disabled={!canContinue || !integration?.id} onClick={() => { void handleContinue(); }}>
          Continue
        </Button>
      </div>
    </div>
  );
};

export default SegmentSteps;
